"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.StakeAccount = void 0;
const web3_js_1 = require("@solana/web3.js");
const anchor_1 = require("@project-serum/anchor");
const spl_token_1 = require("@solana/spl-token");
const common_1 = require("../common");
const hooks_1 = require("../common/hooks");
const auth_1 = require("../auth");
class StakeAccount {
    /**
     * Creates an instance of StakeAccount.
     * @private
     * @param {Program<StakeIdl>} program
     * @param {StakeAccountAddresses} addresses
     * @param {StakeAccountInfo} stakeAccount
     * @param {VoterWeightRecord} voterWeightRecord
     * @memberof StakeAccount
     */
    constructor(program, addresses, stakeAccount, voterWeightRecord) {
        this.program = program;
        this.addresses = addresses;
        this.stakeAccount = stakeAccount;
        this.voterWeightRecord = voterWeightRecord;
    }
    /**
     * TODO:
     * @static
     * @param {Program<StakeIdl>} stakeProgram
     * @param {PublicKey} stakePool
     * @param {PublicKey} owner
     * @returns {StakeAccountAddresses}
     * @memberof StakeAccount
     */
    static deriveAccounts(stakeProgram, stakePool, owner) {
        const stakeAccount = (0, common_1.findDerivedAccount)(stakeProgram.programId, stakePool, owner);
        const voterWeightRecord = (0, common_1.findDerivedAccount)(stakeProgram.programId, "voter-weight-record", stakeAccount);
        return { stakeAccount, voterWeightRecord };
    }
    /**
     * TODO:
     * @static
     * @param {Program<StakeIdl>} stakeProgram
     * @param {PublicKey} stakePool
     * @param {PublicKey} owner
     * @returns {Promise<StakeAccount>}
     * @memberof StakeAccount
     */
    static async load(stakeProgram, stakePool, owner) {
        const addresses = this.deriveAccounts(stakeProgram, stakePool, owner);
        const stakeAccount = (await stakeProgram.account.stakeAccount.fetch(addresses.stakeAccount));
        const voterWeightRecord = (await stakeProgram.account.voterWeightRecord.fetch(addresses.voterWeightRecord));
        return new StakeAccount(stakeProgram, addresses, stakeAccount, voterWeightRecord);
    }
    /**
     * TODO:
     * @static
     * @param {Program<StakeIdl>} stakeProgram
     * @param {PublicKey} stakePool
     * @param {PublicKey} owner
     * @returns {Promise<boolean>}
     * @memberof StakeAccount
     */
    static async exists(stakeProgram, stakePool, owner) {
        const addresses = this.deriveAccounts(stakeProgram, stakePool, owner);
        const stakeAccount = await stakeProgram.provider.connection.getAccountInfo(addresses.stakeAccount);
        return stakeAccount !== null;
    }
    /**
     * TODO:
     * @static
     * @param {Program<StakeIdl>} [stakeProgram]
     * @param {StakePool} [stakePool]
     * @param {(PublicKey | null)} [wallet]
     * @returns {(StakeAccount | undefined)}
     * @memberof StakeAccount
     */
    static use(stakeProgram, stakePool, wallet) {
        var _a;
        return hooks_1.Hooks.usePromise(async () => stakeProgram && stakePool && wallet && StakeAccount.load(stakeProgram, stakePool.addresses.stakePool, wallet), [stakeProgram, (_a = stakePool === null || stakePool === void 0 ? void 0 : stakePool.addresses.stakePool) === null || _a === void 0 ? void 0 : _a.toBase58(), wallet === null || wallet === void 0 ? void 0 : wallet.toBase58()]);
    }
    /**
     * TODO:
     * @static
     * @param {StakeAccount} [stakeAccount]
     * @param {StakePool} [stakePool]
     * @returns {StakeBalance}
     * @memberof StakeAccount
     */
    static useBalance(stakeAccount, stakePool) {
        let stakedJet;
        let unbondingJet;
        if (!!stakePool && !!stakeAccount) {
            if (stakePool.stakePool.bonded.shares.isZero()) {
                stakedJet = new anchor_1.BN(0);
            }
            else {
                stakedJet = stakeAccount.stakeAccount.bondedShares;
            }
            unbondingJet = stakeAccount.stakeAccount.unbondingShares;
        }
        return {
            stakedJet,
            unbondingJet
        };
    }
    /**
     * TODO:
     * @static
     * @param {Program<StakeIdl>} stakeProgram
     * @param {PublicKey} stakePool
     * @param {PublicKey} owner
     * @param {PublicKey} payer
     * @returns {Promise<string>}
     * @memberof StakeAccount
     */
    static async create(stakeProgram, stakePool, owner, payer) {
        const instructions = [];
        await this.withCreate(instructions, stakeProgram, stakePool, owner, payer);
        return stakeProgram.provider.send(new web3_js_1.Transaction().add(...instructions));
    }
    /**
     * TODO:
     * @static
     * @param {Provider} provider
     * @param {StakePool} stakePool
     * @param {ProgramAccount<Realm>} realm
     * @param {PublicKey} owner
     * @param {PublicKey} collateralTokenAccount
     * @param {BN} amount
     * @returns {Promise<string>}
     * @memberof StakeAccount
     */
    static async addStake(provider, stakePool, owner, collateralTokenAccount, amount) {
        const instructions = [];
        await this.withCreate(instructions, stakePool.program, stakePool.addresses.stakePool, owner, owner);
        await this.withAddStake(instructions, stakePool, owner, owner, collateralTokenAccount, amount);
        return provider.send(new web3_js_1.Transaction().add(...instructions));
    }
    /**
     * TODO:
     * @static
     * @param {TransactionInstruction[]} instructions
     * @param {Program<StakeIdl>} stakeProgram
     * @param {PublicKey} stakePool
     * @param {PublicKey} owner
     * @memberof StakeAccount
     */
    static async withCreate(instructions, stakeProgram, stakePool, owner, payer) {
        const addresses = this.deriveAccounts(stakeProgram, stakePool, owner);
        const info = await stakeProgram.provider.connection.getAccountInfo(addresses.stakeAccount);
        // It would be nice to have seperate options
        // 1) The account is from the right program. Relevant to non PDAs
        // 2) The account is the right type. Relevant to non PDAs
        // 3) If the account doesn't exist, should we create it. Relevant to caller
        // 4) If the account exists, should we error or not
        if (!info) {
            const auth = auth_1.Auth.deriveUserAuthentication(owner);
            const ix = await stakeProgram.methods
                .initStakeAccount()
                .accounts({
                owner,
                auth,
                stakePool,
                stakeAccount: addresses.stakeAccount,
                voterWeightRecord: addresses.voterWeightRecord,
                payer,
                systemProgram: web3_js_1.SystemProgram.programId
            })
                .instruction();
            instructions.push(ix);
        }
    }
    /**
     * TODO:
     * @static
     * @param {TransactionInstruction[]} instructions
     * @param {StakePool} stakePool
     * @param {PublicKey} owner
     * @param {PublicKey} payer
     * @param {PublicKey} tokenAccount
     * @param {BN} amount
     * @memberof StakeAccount
     */
    static async withAddStake(instructions, stakePool, owner, payer, tokenAccount, amount = null) {
        const addresses = this.deriveAccounts(stakePool.program, stakePool.addresses.stakePool, owner);
        const ix = await stakePool.program.methods
            .addStake(amount)
            .accounts({
            stakePool: stakePool.addresses.stakePool,
            stakePoolVault: stakePool.addresses.stakePoolVault,
            stakeAccount: addresses.stakeAccount,
            voterWeightRecord: addresses.voterWeightRecord,
            maxVoterWeightRecord: stakePool.stakePool.maxVoterWeightRecord,
            payer,
            payerTokenAccount: tokenAccount,
            tokenProgram: spl_token_1.TOKEN_PROGRAM_ID
        })
            .instruction();
        instructions.push(ix);
    }
}
exports.StakeAccount = StakeAccount;
//# sourceMappingURL=stakeAccount.js.map